import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Post, PostReaction } from '../../../types/post';
import { Connection } from '../../../redux/slices/connectionsSlice';
import { RootState } from '../../../redux/reducers';
import ModalLayout from '../layouts/ModalLayout';
import Avatar from '../misc/Avatar';

interface PostReactionsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  post: Post;
}

function resolveReactorName(
  reaction: PostReaction,
  post: Post,
  connections: Connection[],
  unknownLabel: string,
  currentUser?: { userId?: number; firstName?: string; lastName?: string },
): string {
  if (currentUser?.userId === reaction.userId && currentUser.firstName && currentUser.lastName) {
    return `${currentUser.firstName} ${currentUser.lastName}`;
  }
  if (post.author.userId === reaction.userId) {
    return `${post.author.firstName} ${post.author.lastName}`;
  }
  const connection = connections.find((c) => c.userId === reaction.userId);
  if (connection) {
    return `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`.trim();
  }
  return unknownLabel;
}

export default function PostReactionsModal({
  open,
  setOpen,
  post,
}: PostReactionsModalProps): JSX.Element {
  const { t } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);
  const currentUser = useSelector((state: RootState) => ({
    userId: state.application.userId,
    firstName: state.application.firstName,
    lastName: state.application.lastName,
  }));

  const grouped = useMemo(() => {
    const map = new Map<string, PostReaction[]>();
    post.reactions.forEach((r) => {
      const list = map.get(r.emoji) ?? [];
      list.push(r);
      map.set(r.emoji, list);
    });
    return Array.from(map.entries());
  }, [post.reactions]);

  return (
    <ModalLayout
      closeButton
      open={open}
      setOpen={setOpen}
      className="z-50 mx-6 flex w-full max-w-md flex-col gap-5 rounded-2xl bg-secondary-50 px-6 py-6">
      <h3 className="text-lg font-semibold text-primary-900">
        {t('page.communicationOverview.posts.reactionsModal.title')}
      </h3>
      {grouped.length === 0 ? (
        <p className="text-sm text-primary-600">
          {t('page.communicationOverview.posts.reactionsModal.empty')}
        </p>
      ) : (
        <div className="flex max-h-96 flex-col gap-4 overflow-y-auto">
          {grouped.map(([emoji, reactions]) => (
            <div key={emoji} className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-primary-800">
                <span className="mr-1.5">{emoji}</span>
                {reactions.length}
              </p>
              <ul className="flex flex-col gap-2">
                {reactions.map((reaction: PostReaction) => {
                  const name = resolveReactorName(
                    reaction,
                    post,
                    connections,
                    t('page.communicationOverview.posts.reactionsModal.unknownMember'),
                    currentUser,
                  );
                  const connection = connections.find((c) => c.userId === reaction.userId);
                  const picture =
                    reaction.userId === post.author.userId
                      ? (post.author.picture ?? undefined)
                      : connection?.picture;
                  return (
                    <li key={reaction.id} className="flex items-center gap-2">
                      <Avatar alias={name} src={picture} className="h-8 w-8 shrink-0" />
                      <span className="text-sm text-primary-900">{name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </ModalLayout>
  );
}
